import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { LoaderFunction, Navigate, RouteObject } from 'react-router-dom'
import { Currency } from '@sequencehq/api/utils'
import { format, startOfMonth } from '@sequencehq/utils/dates'
import { endOfMonth } from '@sequencehq/utils/dates'

export const REV_REC_ROUTES = {
  basePath: 'revenue-recognition',
  revenueWaterfall: 'revenue-waterfall',
  deferredRevenue: 'deferred-revenue',
  journalReports: 'journal-reports',
  analysisReports: 'analysis-reports',
  overview: 'overview'
} as const

export const REVENUE_WATERFALL_PATH = `/${REV_REC_ROUTES.basePath}/${REV_REC_ROUTES.revenueWaterfall}`

export const REV_REC_ROUTES_LABELS = {
  [REV_REC_ROUTES.basePath]: null,
  [REV_REC_ROUTES.overview]: null,
  [REV_REC_ROUTES.revenueWaterfall]: 'Revenue waterfall',
  [REV_REC_ROUTES.deferredRevenue]: 'Deferred revenue',
  [REV_REC_ROUTES.journalReports]: 'Journal reports',
  [REV_REC_ROUTES.analysisReports]: 'Analysis reports'
} as const

/**
 * This timeout function will not 'cancel' the request being made by the API package,
 * it's instead used to give us a maximum wait time for our loader, before we
 * give up and continue with the standard load. This doesn't impact the total
 * load time, but we don't want to leave the user waiting for too long after a
 * click before responding to them.
 * @param timeout
 * @returns
 */
const requestWithTimeout =
  (timeout: number) =>
  (request: Promise<unknown>): Promise<unknown> =>
    Promise.race([request, new Promise(res => setTimeout(res, timeout))])

const loadDefaultCurrency = async (): Promise<Currency> => {
  const settings = (await apiQueryClient.fetchQuery({
    queryKey: dashboardv20240509Client.getSequenceAccountSettings.queryKey,
    queryFn: async () => {
      return (
        await dashboardv20240509Client.getSequenceAccountSettings.request()
      )?.data
    },
    staleTime: 60000
  })) as { enabledCurrencies?: Currency[] } | undefined

  const enabled = settings?.enabledCurrencies || []
  if (enabled.includes('USD' as Currency)) return 'USD' as Currency
  return (enabled[0] as Currency) ?? ('USD' as Currency)
}

const preloadJournalReports = async ({
  request
}: Parameters<LoaderFunction>[0]) => {
  await requestWithTimeout(500)(
    Promise.all([
      /**
       * Preload the accounting periods
       */
      apiQueryClient.prefetchQuery({
        queryKey: dashboardv99990101Client.getAccountingPeriods.queryKey,
        queryFn: async () => {
          return (await dashboardv99990101Client.getAccountingPeriods.request())
            ?.data
        },
        staleTime: 60000
      }),
      (async () => {
        const searchParams = new URL(request.url).searchParams
        const params = {
          from:
            searchParams.get('startDate') ??
            format(startOfMonth(new Date()), 'yyyy-MM-dd'),
          to:
            searchParams.get('endDate') ??
            format(endOfMonth(new Date()), 'yyyy-MM-dd'),
          currency: searchParams.get('currency') ?? 'USD'
        }

        await requestWithTimeout(500)(
          apiQueryClient.prefetchQuery({
            queryKey: [
              ...dashboardv99990101Client.getJournalsPaginated.queryKey,
              params
            ],
            queryFn: async () => {
              return (
                await dashboardv99990101Client.getJournalsPaginated.request({
                  from: params.from as string,
                  to: params.to as string,
                  currency: params.currency as Currency
                })
              )?.data
            },
            staleTime: 5000
          })
        )

        return true
      })(),
      /**
       * Preload graph data - which relies on the currency selection.
       */
      (async () => {
        const selectedCurrency =
          new URL(request.url).searchParams.get('currency') ??
          (await loadDefaultCurrency())

        const startDate =
          new URL(request.url).searchParams.get('startDate') ?? undefined
        const endDate =
          new URL(request.url).searchParams.get('endDate') ?? undefined

        await apiQueryClient.prefetchQuery({
          queryKey: [
            ...dashboardv99990101Client.getRevRecChartsData.queryKey,
            {
              currency: selectedCurrency,
              from: startDate,
              to: endDate
            }
          ],
          staleTime: 5000,
          queryFn: async () => {
            return (
              await dashboardv99990101Client.getRevRecChartsData.request({
                currency: selectedCurrency as Currency,
                from: startDate,
                to: endDate
              })
            )?.data
          }
        })
      })()
    ])
  )

  return true
}

export const revenueRecognitionRoutes: RouteObject[] = [
  {
    path: REV_REC_ROUTES.basePath,
    lazy: async () => {
      const { RevRecRoot } = await import('./view/RevenueRecognition.root')
      return {
        Component: RevRecRoot,
        loader: async () => {
          /**
           * These routers have a 'loader' mechanism - this data is designed
           * to load the critical path data (with a timeout) for the page before we navigate.
           * We don't access this data directly - it instead if used to pre-populate the API
           * cache with fresh data.
           *
           * This reduces the 'flash' of loading seen when moving to a page, unless our API calls
           * are particularly slow.
           */

          /**
           * Preloads the default currency selection.
           */
          await requestWithTimeout(500)(loadDefaultCurrency())

          return true
        }
      }
    },
    children: [
      {
        index: true,
        loader: preloadJournalReports,
        element: <Navigate to={REV_REC_ROUTES.overview} />
      },
      {
        path: REV_REC_ROUTES.overview,
        lazy: async () => {
          const { Overview } = await import('./view/overview/Overview.root')
          return {
            Component: Overview,
            loader: preloadJournalReports
          }
        }
      },
      {
        path: REV_REC_ROUTES.revenueWaterfall,
        children: [
          {
            index: true,
            lazy: async () => {
              const { RevenueWaterfall } = await import(
                './view/revenueWaterfall/RevenueWaterfall.root'
              )
              return {
                Component: RevenueWaterfall
              }
            }
          },
          {
            path: 'download',
            lazy: async () => {
              const { WaterfallReportDownload } = await import(
                './view/waterfallReportDownload/WaterfallReportDownload'
              )
              return {
                Component: WaterfallReportDownload
              }
            }
          }
        ]
      },
      {
        path: REV_REC_ROUTES.journalReports,
        children: [
          {
            index: true,
            lazy: async () => {
              const { RevenueRecognitionDashboard } = await import(
                './view/revenueRecognitionDashboard/RevenueRecognitionDashboard.root'
              )
              return {
                Component: RevenueRecognitionDashboard,
                loader: preloadJournalReports
              }
            }
          },
          {
            path: 'download',
            lazy: async () => {
              const { JournalReportDownload } = await import(
                './view/journalReportDownload/JournalReportDownload'
              )
              return {
                Component: JournalReportDownload
              }
            }
          }
        ]
      },
      {
        path: REV_REC_ROUTES.deferredRevenue,
        children: [
          {
            index: true,
            lazy: async () => {
              const { DeferredRevenueDashboard } = await import(
                './view/deferredRevenueDashboard/DeferredRevenueDashboard.root'
              )
              return {
                Component: DeferredRevenueDashboard
              }
            }
          },
          {
            path: 'download',
            lazy: async () => {
              const { DeferredRevenueScheduleDownload } = await import(
                './view/deferredRevenueScheduleDownload/DeferredRevenueScheduleDownload'
              )
              return {
                Component: DeferredRevenueScheduleDownload
              }
            }
          }
        ]
      },
      {
        path: REV_REC_ROUTES.analysisReports,
        children: [
          {
            path: 'download',
            lazy: async () => {
              const { AnalysisReportDownload } = await import(
                './view/analysisReportDownload/AnalysisReportDownload'
              )
              return {
                Component: AnalysisReportDownload
              }
            }
          }
        ]
      }
    ]
  }
]
